import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, MessageCircle } from 'lucide-react';

const FIELDS = [
  { label: 'WALLET_HANDLE', placeholder: '0x... or @handle' },
  { label: 'BUDGET_SCOPE', placeholder: 'Commission details / project scope' },
  { label: 'CHANNEL_PREF', placeholder: 'Telegram / Signal / Other' },
];

function ContactTerminalHeader() {
  return (
    <div className="contact-terminal-header">
      <div className="flex gap-1.5">
        <span className="terminal-dot terminal-dot--gold" />
        <span className="terminal-dot terminal-dot--yellow" />
        <span className="terminal-dot terminal-dot--green" />
      </div>
      <span className="font-mono text-xs text-white/30 ml-4">ZATO::SECURE_CHANNEL</span>
    </div>
  );
}

function ContactTerminalIntro() {
  return (
    <>
      <p className="font-mono text-sm text-[#FFC700] mb-2">
        <span className="text-white/30">$ </span>initialise --secure-channel --protocol=telegram
      </p>
      <p className="font-mono text-xs text-white/60 mb-8">
        &gt; CHANNEL: TELEGRAM<br />
        &gt; IDENTITY: NOT REQUIRED BY ZATO<br />
        &gt; NOTE: Telegram is not end-to-end encrypted by default and may retain account/metadata.<br />
        &gt; STATUS: <span className="text-[#00ff99] font-bold">OPEN</span>
      </p>
    </>
  );
}

function ContactInfoBlock() {
  return (
    <div className="mb-8" data-testid="contact-info">
      <p className="body-text text-white/60 mb-4 text-sm">
        Commissions and inquiries are handled through Telegram — no email, no forms.
      </p>
      <p className="font-mono text-xs text-white/30 mb-2">PARAMETERS_WELCOME:</p>
      <ul className="font-mono text-xs text-white/40 space-y-1 ml-4 mb-6">
        {FIELDS.map(f => (
          <li key={f.label}>
            <span className="text-[#FFC700]/60">[{f.label}]</span> — {f.placeholder}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContactCTA() {
  return (
    <a
      href="https://t.me/ZatoUK"
      target="_blank"
      rel="noopener noreferrer"
      className="contact-cta"
      data-testid="contact-telegram-link"
    >
      <MessageCircle size={16} />
      <span>OPEN SECURE CHANNEL</span>
      <span className="font-mono text-xs text-white/40">t.me/ZatoUK</span>
      <Send size={14} className="ml-auto" />
    </a>
  );
}

export function ContactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="contact" className="py-32 px-6 relative overflow-hidden" data-testid="contact-section" ref={ref}>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FFC700]/30 to-transparent" />

      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <span className="mono-tag text-[#FFC700]">// SECTION :: SECURE_CHANNEL</span>
          <h2 className="section-title mt-4">
            SECURE<br /><span className="text-[#FFC700]">CHANNEL</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="contact-terminal"
          data-testid="contact-terminal"
        >
          <ContactTerminalHeader />

          <div className="contact-terminal-body">
            <ContactTerminalIntro />
            <ContactInfoBlock />
            <ContactCTA />

            <p className="font-mono text-[10px] text-white/15 mt-6 text-center">
              // NO PERSONAL DETAILS REQUIRED — WALLET OR HANDLE SUFFICIENT
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
